/**
 * Label resolution and plain text (architecture §11.2; ADR-0040 decision 15). Every label-producing renderer takes a
 * MANDATORY `LabelResolver` (a type error without one), bound by the calling job or route to the output's principal:
 * the recipient for email, the requester for exports, the credential or webhook scope for API payloads. A resolver
 * returns `undefined` for anything that principal can't see; renderers then print a neutral placeholder.
 */
import type { DocJSON, Reference } from './doc';

export interface LabelResolver {
  /** Display name of a user the principal can see. */
  user(id: string): string | undefined;
  issue(id: string): { identifier: string; title: string } | undefined;
  project(id: string): { name: string } | undefined;
  document(id: string): { title: string } | undefined;
  /** Absolute URL of an entity or file (email, markdown); undefined renders no link. */
  url?(kind: Reference['kind'] | 'file', id: string): string | undefined;
}

export const PRIVATE_LABEL: Readonly<Record<Reference['kind'], string>> = {
  user: 'Unknown user',
  issue: 'Private issue',
  project: 'Private project',
  document: 'Private document',
};

/** The label a principal may see for a mention (`visible`), or the neutral placeholder. */
export function resolveMention(kind: Reference['kind'], id: string, labels: LabelResolver): { label: string; visible: boolean } {
  let label: string | undefined;
  switch (kind) {
    case 'user': {
      const n = labels.user(id);
      label = n === undefined ? undefined : `@${n}`;
      break;
    }
    case 'issue': {
      const i = labels.issue(id);
      label = i ? `${i.identifier} ${i.title}` : undefined;
      break;
    }
    case 'project':
      label = labels.project(id)?.name;
      break;
    case 'document':
      label = labels.document(id)?.title;
      break;
  }
  return label === undefined ? { label: PRIVATE_LABEL[kind] ?? 'Private item', visible: false } : { label, visible: true };
}

const BLOCKS = new Set(['paragraph', 'heading', 'blockquote', 'codeBlock', 'listItem', 'taskItem', 'horizontalRule', 'image']);

/**
 * Plain text for search and previews. Mentions render as their KIND placeholder, never a label (`labels` is required
 * by signature but unused), so a body can't be found by a hidden entity's title.
 */
export function toPlainText(doc: DocJSON, labels: LabelResolver): string {
  void labels;
  const out: string[] = [];
  const visit = (n: DocJSON): void => {
    switch (n.type) {
      case 'text':
        out.push(n.text ?? '');
        return;
      case 'hardBreak':
        out.push('\n');
        return;
      case 'mention': {
        const kind = typeof n.attrs?.kind === 'string' ? n.attrs.kind : 'user';
        out.push(kind === 'user' ? '@user' : `[${kind}]`);
        return;
      }
      case 'image':
        out.push('[image]');
        break;
      default:
        break;
    }
    for (const c of n.content ?? []) visit(c);
    if (BLOCKS.has(n.type)) out.push('\n');
  };
  visit(doc);
  return out
    .join('')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
